import { useEffect , useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { IReduxAppState } from "../reducers";
import { useAPIV2 } from '../yoello-lib/modules/API2/useAPIv2';
import APIExtensions from '../yoello-lib/modules/API2/APIDefs';
import { HTTPMethods } from "../yoello-lib/modules/API/API";
import CryptoJS from "crypto-js";
import { JSEncrypt } from "jsencrypt";
import { setRSAKey } from "../actions/openBanking";

interface ISecureKey {
    rsa:string;
}

interface ICreateAccountData {
    account_name: string
    account_number: string
    bank_name: string
    enabled: boolean
    sort_code: string
}
export interface IResponse {
    data?:any;
    isSuccessful?:boolean;
}

export function useOpenBanking() {
    const selectedVenueId = useSelector((state: IReduxAppState) => state.selectedVenueId);
    const secureKey = useSelector((state: IReduxAppState) => state.openBanking);
    const [account, setAccount] = useState<Partial<ICreateAccountData> | undefined>(undefined);
    const [accountLoading, setAccountLoading] = useState<boolean>(false);
    const {apiData:config } = useAPIV2<ISecureKey>(APIExtensions.config, {
        canAPIGetCacheValue: true
    });

    const { callAPI } = useAPIV2<IResponse>(
        APIExtensions.account,
        {
            query: `${selectedVenueId}/integrations/payments/yapily?include_disabled=true`,
            canAPIGetCacheValue: true,
        }
    );

    const dispatch = useDispatch();
    useEffect(()=>{
        if(config){
            dispatch(setRSAKey(config.rsa))
        }
     // eslint-disable-next-line    
    },[config])
    useEffect(()=>{
        fetchAccountData();
     // eslint-disable-next-line    
    },[]);

    async function fetchAccountData(){
        try{
            setAccountLoading(true);
            const account = await callAPI(HTTPMethods.GET);
            if(account.isSuccessful){
                setAccount(account.data as Partial<ICreateAccountData>);
                setAccountLoading(false);
            }else{
                setAccount(undefined)
                setAccountLoading(false);
            }
        }catch(err){
            setAccount(undefined)
            setAccountLoading(false);
        }
    }

    function getEncryptedData(data:ICreateAccountData){
        const { RSAKey } = secureKey; 
        const { account_name , account_number, bank_name , sort_code} = data;
        
        if(RSAKey){
            const encrypt = new JSEncrypt({
                default_key_size:'4096'
            })
            encrypt.setPublicKey(RSAKey)
           
            const key = CryptoJS.lib.WordArray.random(32); //Use a random key
            const iv = CryptoJS.lib.WordArray.random(16);
            let encryptObject = {
                account_name,
                bank_name,
                sort_code : sort_code.replaceAll("-","")
            }
            if(!account_number.includes("*")){
                // @ts-ignore
                encryptObject.account_number = account_number
            }
            const encrypted = CryptoJS.AES.encrypt(JSON.stringify(encryptObject), key, {
                    iv: iv
                });
            const encriptedData = JSON.stringify({
                "enc_session_key": encrypt.encrypt(key.toString(CryptoJS.enc.Base64)),
                "iv": iv.toString(CryptoJS.enc.Base64),
                "ciphertext": encrypted.ciphertext.toString(CryptoJS.enc.Base64),
            });

            return encriptedData
        }
    }

    async function handleAccount(data:ICreateAccountData):Promise<IResponse>{
        const method = account ? HTTPMethods.PATCH:HTTPMethods.POST;
        const ENCRYPTED_DATA = getEncryptedData(data);
        try {
        const response = await callAPI(method,{
                body:{
                  integration_partner_name: "yapily",
                  account_details: ENCRYPTED_DATA,
                  enabled: data.enabled
                }
             });
             if(response.isSuccessful){
                fetchAccountData();
             }
             return response;
        }catch(error){
            return {
                isSuccessful:false,
                data:error
            }
        }
       
    }

    return { 
        handleAccount , 
        account,
        accountLoading,
        fetchAccountData
    }
}